import { GROUND_TERMINAL_UID, Net, createTUID, splitMultipartUID, } from "@buildwithflux/models";
import Graph from "graphology";
import { connectedComponents } from "graphology-components";
import { mapValues } from "lodash";
import { ElementHelper } from "../../elements";
// TODO nets are recalculated every time I move any item on schematic, even if only
// vertex positions (or even just element positions!) change. That doesn't seem smart
// at all. (Also happens in main; not a regression in this PR).
// QUESTION: is this comment still relevant? If so, what is the current status of this issue?
/** Given a set of vertices connected to terminals and each other, return the groups of connected terminals (i.e. nets).
 *
 * Note 1: Specifying `elements` is necessary to determine which terminals are attached to ground or portals.
 * Note 2: Specifying `delimiter` is needed to distinguish between the ':' delimiters used in simulator (and returned by
 * the simulator data cloud function) and '__' used elsewhere (and persisted in the form of pcb node ids). It's possible
 * we could migrate to use '__' everywhere, but I haven't looked into how deep that rabbit hole goes.
 */
export function generateNetInstances(elements, vertices) {
    const graph = new Graph({ multi: false, type: "undirected" });
    const terminalElementIds = {};
    const portalIds = new Set();
    for (const vertex of Object.values(vertices)) {
        // NOTE: `connectedVertices` might be missing for some old doc, and so we want
        // to fallback to empty object here to make sure it wont break the process
        // See note: https://coda.io/d/_dHbnnnWufWJ/Failure-in-V0-snapshot_suhMl#_lueul
        for (const other of Object.keys(vertex.connectedVertices || {})) {
            graph.mergeEdge(vertex.id, other);
        }
        if (!vertex.terminal)
            continue;
        const terminalId = createTUID(vertex.terminal);
        const element = elements[vertex.terminal.element_uid];
        let portalId;
        if (!element) {
            // Some documents have invalid data, where an element has been removed but the vertex left in place.
            // FIXME: This error output has been disabled because it is happening so frequently that it is burning our Sentry budget.
            //        The underlying cause that is leaving vertex data lying around in the document still needs to be fixed,
            //        and is tracked in https://github.com/buildwithflux/flux-app/issues/6047.
            // Note: one (maybe not the only) reproduceable cause of this was branchpoints getting "requested" by
            // RouteManager.ts but not added in applyVertexUpdates (due to the fragile diff iterating logic). This
            // specific case is now fixed.
            // console.error(
            //    `Vertex ${vertex.id} is connected to non-existent element ${vertex.terminal.element_uid}:${vertex.terminal.terminal_uid}`,
            // );
        }
        const placeholderTerminalId = "SUBMODULE_PORTAL_PLACEHOLDER";
        const [parentUid, _] = splitMultipartUID(vertex.terminal.element_uid);
        // Note: Wiring:getAllNetVerticesAcrossPortals() also constructs a portalId for this purpose.
        // If you change it here, change it there! TODO we should consolidate these two places.
        if (element && ElementHelper.isPortal(element)) {
            // Make all same-named portals equivalent so they're connected.
            // Group by same subdoc (needed for in simulator, which uses nested parts).
            if (parentUid) {
                // For submodule portals, include a placeholder terminal id, so it has at least 3 parts,
                // (which is how the Net's topLevelTerminals() method distinguishes them) while still
                // being named the same as each other.
                const portalElementId = createTUID([parentUid, element.label ?? ""]);
                portalId = createTUID([portalElementId, placeholderTerminalId]);
            }
            else {
                // For top-level portals (which are used in the net hash calc), we don't
                // include a placeholder terminal id, for historically-stable hashes.
                portalId = createTUID(["", element.label ?? ""]); // "" for historically-stable hash
            }
        }
        else if (element && ElementHelper.isGroundElement(element)) {
            // Same basic logic as for portals; slightly different string format.
            if (parentUid) {
                const portalElementId = createTUID([parentUid, GROUND_TERMINAL_UID]);
                portalId = createTUID([portalElementId, placeholderTerminalId]);
            }
            else {
                // Different format than non-ground portalId for historically-stable hash.
                portalId = GROUND_TERMINAL_UID;
            }
        }
        terminalElementIds[terminalId] = vertex.terminal.element_uid;
        graph.mergeEdge(vertex.id, terminalId);
        // Note: conceptually, we also want to connect the outer terminal ("elementUid:terminalUid") to
        // the submodule's same inner terminal ("elementUid:terminalUid:genesisUid"), to connect the outer
        // nets correctly to the inner nets. However, if the second arg is a genesis uid, then createTUID()
        // returns only the base uid, so the inner terminal will also be simply "elementUid:terminalUid",
        // meaning the nets are already connected and the below is a no-op. But it's an important part of
        // the semantics here, so leave this comment for clarity!
        //
        // graph.mergeEdge(terminalId, createTUID(terminalId, GENESIS_TERMINAL_TERMINAL_UID, delimiter));
        if (portalId) {
            // Merge grounds and portals to a common node (grouped as above).
            graph.mergeEdge(terminalId, portalId);
            portalIds.add(portalId);
            terminalElementIds[portalId] = vertex.terminal.element_uid;
        }
    }
    const subGraphs = connectedComponents(graph);
    const nets = [];
    for (const nodeIds of subGraphs) {
        const terminals = Object.fromEntries(nodeIds.filter((id) => id in terminalElementIds).map((id) => [id, terminalElementIds[id]]));
        const net = new Net(terminals);
        if (net.terminals.size > 0) {
            // Exclude nets that are only branchpoints and don't have any real terminals.
            nets.push(net);
        }
    }
    return nets;
}
/**
 * NOTE: The caller should merge in subDocumentData if they want connectivity across submodules.
 */
export function generateNetInstanceMap(elements, vertices) {
    const nets = generateNetInstances(elements, vertices);
    return Object.fromEntries(nets.map((n) => [n.getHash(), n]));
}
/**
 * NOTE: The caller should NOT merge in subDocumentData.
 */
export function generateTopLevelNetMap(elements, vertices) {
    const nets = generateNetInstanceMap(elements, vertices);
    return mapValues(nets, (n) => Net.terminalsToMap(n.topLevelTerminals()));
}
//# sourceMappingURL=netMapping.js.map